import React, { useEffect, useState } from "react";
import Button from "@leafygreen-ui/button";
import TextInput from "@leafygreen-ui/text-input";
import { Body, H3 } from "@leafygreen-ui/typography";
import { useApi } from "../providers/Api";

const normalizeEmail = email => email.trim().toLowerCase();

export default function EditorAccess({ resourceType, resourceId }) {
  const { getResourceEditors, updateResourceEditors } = useApi();
  const [editors, setEditors] = useState([]);
  const [pendingEditors, setPendingEditors] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    getResourceEditors(resourceType, resourceId)
      .then(data => {
        if (!active) return;
        setEditors(data.editors || []);
        setPendingEditors(data.pendingEditors || []);
      })
      .catch(() => {
        if (active) setError("Unable to load editor access.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [resourceType, resourceId]);

  const addEmail = () => {
    const normalized = normalizeEmail(email);
    if (!/^[^@\s]+@mongodb\.com$/.test(normalized)) {
      setError("Enter a valid @mongodb.com email address.");
      return;
    }
    if (pendingEditors.includes(normalized) || editors.some(editor => normalizeEmail(editor.email || "") === normalized)) {
      setError("This person already has editor access.");
      return;
    }
    setPendingEditors(current => [...current, normalized]);
    setEmail("");
    setError("");
    setNotice("");
  };

  const removeEditor = userId => setEditors(current => current.filter(editor => editor.userId !== userId));
  const removePending = value => setPendingEditors(current => current.filter(item => item !== value));

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await updateResourceEditors(resourceType, resourceId, {
        editorIds: editors.map(editor => editor.userId),
        pendingEmails: pendingEditors
      });
      const refreshed = await getResourceEditors(resourceType, resourceId);
      setEditors(refreshed.editors || []);
      setPendingEditors(refreshed.pendingEditors || []);
      setNotice("Editor access updated.");
    } catch (e) {
      setError(e.message || "Unable to update editor access.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ margin: "24px 0", padding: "16px", border: "1px solid #e8edeb", borderRadius: "8px" }}>
      <H3>Editors</H3>
      <Body>Editors can update this item, but cannot delete it or manage editor access. Only @mongodb.com accounts can be added.</Body>
      {loading ? <p>Loading editor access…</p> : (
        <>
          <ul>
            {editors.map(editor => (
              <li key={editor.userId}>
                {editor.name}{editor.email ? ` (${editor.email})` : ""} — Editor{" "}
                <button type="button" onClick={() => removeEditor(editor.userId)} aria-label={`Remove ${editor.email || editor.name}`}>
                  Remove
                </button>
              </li>
            ))}
            {pendingEditors.map(pendingEmail => (
              <li key={pendingEmail}>
                {pendingEmail} — Unconfirmed{" "}
                <button type="button" onClick={() => removePending(pendingEmail)} aria-label={`Remove pending editor ${pendingEmail}`}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", marginTop: "12px" }}>
            <div style={{ flex: 1 }}>
              <TextInput
                label="Add editor by email"
                placeholder="name@mongodb.com"
                type="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
              />
            </div>
            <Button type="button" variant="primaryOutline" onClick={addEmail}>Add</Button>
          </div>
          <div style={{ marginTop: "12px" }}>
            <Button type="button" variant="primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save editor access"}</Button>
          </div>
          {error && <p role="alert" style={{ color: "#b00020" }}>{error}</p>}
          {notice && <p role="status">{notice}</p>}
        </>
      )}
    </section>
  );
}
